/**
 * Dev-only auth-gate escape hatch: `?dev=1` on the URL.
 *
 * WHY THIS IS A MODULE AND NOT AN INLINE `location.search` READ
 * -----------------------------------------------------------
 * AppShell used to re-derive the flag from `window.location.search` on every
 * render. That works exactly until the first react-router navigation: SPA nav
 * rewrites the URL WITHOUT the query string, so the very next render reads an
 * empty search, the flag flips to false and the shell drops the previewed
 * route for <Login />. The bypass therefore survived only the initial page
 * load — noted on TMA#75.
 *
 * The fix is to treat `?dev=1` as an EVENT (seen once) rather than a state
 * (present in the URL now), latching it for the browser tab. sessionStorage,
 * not localStorage, so it dies with the tab instead of quietly outliving the
 * session that asked for it.
 *
 * PRODUCTION SAFETY — by construction, not by configuration
 * --------------------------------------------------------
 * `import.meta.env.DEV` is statically substituted by Vite at build time, so in
 * a production bundle the first line is `if (!false) return false;` and the
 * rest is dead code the minifier drops. There is no env var, no flag and no
 * deploy-time toggle that can turn this on in production — the same stance as
 * the per-service dev bypasses in `src/env.ts`.
 */
const DEV_BYPASS_KEY = 'qlash.dev_bypass';

/** True when the auth gate should be skipped for local preview. Dev only. */
export function isDevBypassActive(): boolean {
  if (!import.meta.env.DEV) return false;
  const inUrl = new URLSearchParams(window.location.search).has('dev');
  try {
    if (inUrl) {
      window.sessionStorage.setItem(DEV_BYPASS_KEY, '1');
      return true;
    }
    return window.sessionStorage.getItem(DEV_BYPASS_KEY) === '1';
  } catch {
    // Storage unavailable (private mode, blocked cookies): degrade to the old
    // non-sticky behaviour rather than losing the escape hatch entirely.
    return inUrl;
  }
}

/** Clear the latch — for a dev who wants the real auth gate back without a new tab. */
export function clearDevBypass(): void {
  if (!import.meta.env.DEV) return;
  try {
    window.sessionStorage.removeItem(DEV_BYPASS_KEY);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}
